import type { FlockEntry } from "./DailyReportPage";
import { flockDataProblem } from "./flockDataCheck";
import { MORTALITY_CAUSES } from "@/lib/mortalityCauses";

interface Props {
  flocks: FlockEntry[];
  onChange: (index: number, field: keyof FlockEntry, value: string) => void;
}

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-transparent px-3 py-2 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

export default function StepFlockData({ flocks, onChange }: Props) {
  return (
    <div>
      <h4 className="mb-4 text-base font-semibold text-gray-800 dark:text-white/90">
        Daily Flock Data
      </h4>
      <div className="space-y-5">
        {flocks.map((f, i) => (
          <div
            key={f.flock_id}
            className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
          >
            <div className="mb-3 flex items-center justify-between">
              <h5 className="text-sm font-semibold text-gray-800 dark:text-white/90">
                {f.shed_code || f.flock_type}
              </h5>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {f.batch_name}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    f.shed_type === "layer"
                      ? "bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400"
                      : "bg-warning-50 text-warning-600 dark:bg-warning-500/15 dark:text-warning-400"
                  }`}
                >
                  {f.shed_type}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Mortality
                </label>
                <input
                  type="number"
                  min="0"
                  value={f.mortality}
                  onChange={(e) => onChange(i, "mortality", e.target.value)}
                  className={inputClass}
                  placeholder="0"
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  Feed Consumed (kg) *
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={f.feed_consumed_kg}
                  onChange={(e) => onChange(i, "feed_consumed_kg", e.target.value)}
                  className={inputClass}
                  placeholder="e.g. 1500"
                />
              </div>

              {f.shed_type === "layer" && (
                <>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Egg Trays *
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={f.egg_trays}
                      onChange={(e) => onChange(i, "egg_trays", e.target.value)}
                      className={inputClass}
                      placeholder="e.g. 24"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Broken/Defective (eggs)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={f.broken_trays}
                      onChange={(e) => onChange(i, "broken_trays", e.target.value)}
                      className={inputClass}
                      placeholder="0"
                    />
                  </div>
                </>
              )}

              {/* Last in the card: it appears once deaths are typed, and
                  anywhere earlier it pushed every box after it to a new place
                  under the supervisor's thumb (live walk, 2026-10-31). */}
              {parseInt(f.mortality) > 0 && (
                <div>
                  <label
                    htmlFor={`cause-${f.flock_id}`}
                    className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400"
                  >
                    Cause of death
                  </label>
                  <select
                    id={`cause-${f.flock_id}`}
                    value={f.mortality_cause}
                    onChange={(e) => onChange(i, "mortality_cause", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Not chosen</option>
                    {MORTALITY_CAUSES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            {f.current_count <= 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                No birds left in this flock — nothing needs recording.
              </p>
            )}
            {(() => {
              const problem = flockDataProblem(f);
              return problem ? (
                <p role="alert" className="mt-2 text-xs text-error-600 dark:text-error-400">
                  {problem}
                </p>
              ) : null;
            })()}
          </div>
        ))}
      </div>
    </div>
  );
}
